
import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Linkedin, Link, Unlink } from "lucide-react";

export const LinkedInConnection = () => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isLinkedInConnected, setIsLinkedInConnected] = useState(false);
  const { toast } = useToast();

  const checkLinkedInConnection = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.app_metadata?.providers?.includes('linkedin_oidc')) {
        setIsLinkedInConnected(true);
      }
    } catch (error) {
      console.error('Error checking LinkedIn connection:', error);
    }
  };

  const handleConnectLinkedIn = async () => {
    try {
      setIsConnecting(true);
      console.log('Connecting LinkedIn to existing account...');

      const { data, error } = await supabase.auth.linkIdentity({
        provider: 'linkedin_oidc',
      });

      if (error) {
        console.error('LinkedIn connection error:', error);
        throw error;
      }

      console.log('LinkedIn connection initiated successfully');
      
      toast({
        title: "LinkedIn Connected",
        description: "Your LinkedIn account has been successfully connected!",
        className: "bg-green-50 border-green-200",
      });

      setIsLinkedInConnected(true);

    } catch (error: any) {
      console.error('LinkedIn connection failed:', error);
      toast({
        title: "Connection Failed",
        description: error.message || "Failed to connect LinkedIn account",
        variant: "destructive",
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnectLinkedIn = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const linkedInIdentity = user.identities?.find(
        (identity) => identity.provider === 'linkedin_oidc'
      );

      if (!linkedInIdentity) {
        throw new Error('LinkedIn identity not found');
      }

      const { error } = await supabase.auth.unlinkIdentity(linkedInIdentity);

      if (error) throw error;

      toast({
        title: "LinkedIn Disconnected",
        description: "Your LinkedIn account has been disconnected.",
      });

      setIsLinkedInConnected(false);

    } catch (error: any) {
      console.error('LinkedIn disconnection failed:', error);
      toast({
        title: "Disconnection Failed",
        description: error.message || "Failed to disconnect LinkedIn account",
        variant: "destructive",
      });
    }
  };

  // Check connection status on component mount
  useEffect(() => {
    checkLinkedInConnection();
  }, []);

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Linkedin className="h-6 w-6 text-blue-600" />
          <div>
            <h3 className="font-semibold text-lg">LinkedIn Account</h3>
            <p className="text-sm text-gray-600">
              {isLinkedInConnected 
                ? "Your LinkedIn account is connected" 
                : "Connect your LinkedIn account for easy sign-in"
              }
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isLinkedInConnected ? (
            <div className="flex items-center gap-2 text-green-600">
              <Link className="h-4 w-4" />
              <span className="text-sm font-medium">Connected</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-gray-500">
              <Unlink className="h-4 w-4" />
              <span className="text-sm font-medium">Not Connected</span>
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {!isLinkedInConnected ? (
          <Button 
            onClick={handleConnectLinkedIn}
            disabled={isConnecting}
            className="w-full flex items-center gap-2"
          >
            <Linkedin className="h-4 w-4" />
            {isConnecting ? "Connecting..." : "Connect LinkedIn"}
          </Button>
        ) : (
          <Button 
            onClick={handleDisconnectLinkedIn}
            variant="outline"
            className="w-full flex items-center gap-2"
          >
            <Unlink className="h-4 w-4" />
            Disconnect LinkedIn
          </Button>
        )}

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Benefits of connecting LinkedIn:</strong>
          </p>
          <ul className="text-sm text-blue-700 mt-2 space-y-1">
            <li>• Sign in with one click using LinkedIn</li>
            <li>• Keep your current account and all data</li>
            <li>• No need to remember another password</li>
          </ul>
        </div>
      </div>
    </Card>
  );
};
